import { styled, Typography } from "@mui/material";
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { isInWebView, isVirtualKeyboardEnabled } from "../../config/const";
import { VirtualKeyboardRef } from "../VirtualKeyboard";
import { reduce, times } from "lodash";

import color from "color";

const PinInputField = styled("input")(({ theme }) => ({
  color: theme.palette.text.primary,
  borderColor: theme.palette.primary.main,
  background: color(theme.palette.background.default).lighten(0.1).hex(),

  "&:focus": {
    borderColor: theme.palette.primary.main,
    backgroundColor: color(theme.palette.background.default).darken(0.07).hex(),
  },

  "&.error": {
    borderColor: theme.palette.error.main,
  },
}));

interface DebugCompnentProps extends PropsWithChildren<any> {
  value: any;
}
const DebugComponent = ({ value, children }: DebugCompnentProps) => {
  return (
    <Typography variant="h1">
      {children}
      {JSON.stringify(value, null, 2) + ""}
    </Typography>
  );
};

const defaultPinCode = {
  1: "",
  2: "",
  3: "",
  4: "",
  5: "",
  6: "",
};

interface PinInputsProps {
  showValues?: boolean;
  focusedInput: string | undefined;
  setFocusedInput: Dispatch<SetStateAction<string | undefined>>;
  pinCode: typeof defaultPinCode;
  setPinCode: (patch: Partial<typeof defaultPinCode>) => void;
  keyboard: React.RefObject<VirtualKeyboardRef>;
}
const PinInputs = ({
  showValues = true,
  focusedInput,
  setFocusedInput,
  pinCode,
  setPinCode,
  keyboard,
}: PinInputsProps) => {
  const onPasteCapture = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const content = event.clipboardData.getData("Text");

      const isValidPin = /^[0-9]{1,6}$/g.test(content);

      if (!isValidPin) {
        return;
      }

      event.preventDefault();

      const pinCode = reduce(
        times(6),
        (acc, key) => ({
          ...acc,
          [key + 1]: content[key],
        }),
        {},
      );

      setPinCode(pinCode);
    },
    [setPinCode],
  );

  const onFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => {
      const {
        target: { name },
      } = event;

      setFocusedInput(`${+name}`);

      if (keyboard?.current) {
        keyboard?.current.setInput("", name);
      }
    },
    [setFocusedInput],
  );

  const onChange = useCallback(
    (
      event: React.ChangeEvent<
        HTMLInputElement & { name: string; value: string; blur: () => void }
      >,
    ) => {
      const {
        target: { name, value },
      } = event;

      /**
       * Blur when setting last input is set form keyboard
       */
      if (name === "6" && value && !!value.length) {
        event.target.blur();
      }

      const regex = /^\d$/;

      if (value === "" || regex.test(value)) {
        setPinCode({ [name]: value });

        let nextFocusedInput: number | undefined = +name;

        if (name === "6") {
          nextFocusedInput = undefined;
        } else if (value.length) {
          nextFocusedInput += 1;
        } else {
          nextFocusedInput -= 1;
        }

        setFocusedInput(nextFocusedInput ? `${nextFocusedInput}` : undefined);
      }
    },
    [setFocusedInput],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement & { name: string }>) => {
      const {
        code,
        currentTarget: { name },
      } = event;

      switch (code) {
        case "Backspace": {
          event.preventDefault();

          setPinCode({ [name]: "" });

          const nextFocusedInput = Math.max(1, +name - 1);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowRight": {
          event.preventDefault();

          const nextFocusedInput = Math.min(+name + 1, 6);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowLeft": {
          event.preventDefault();
          const nextFocusedInput = Math.max(1, +name - 1);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowUp":
        case "ArrowDown": {
          event.preventDefault();

          break;
        }

        default: {
          break;
        }
      }
    },
    [],
  );

  /**
   * Auto-focus input
   */
  useEffect(() => {
    if (focusedInput) {
      const input = document.querySelector<HTMLInputElement>(
        `input[name="${focusedInput}"]`,
      );

      if (input && typeof input.focus === "function") {
        input.focus();
        input.select();
      }
    }
  }, [focusedInput]);

  const onBeforeInput = useCallback((event: InputEvent) => {
    switch (event.inputType) {
      case "deleteByComposition":
      case "deleteByCut":
      case "deleteByDrag":
      case "deleteEntireSoftLine":
      case "deleteSoftLineBackward":
      case "deleteSoftLineForward":
      case "deleteHardLineBackward":
      case "deleteHardLineForward":
      case "deleteWordBackward":
      case "deleteWordForward":
      case "deleteContent":
      case "deleteContentForward":
      case "deleteContentBackward": {
        event.preventDefault();

        // eslint-disable-next-line prefer-destructuring
        const name = (event?.currentTarget as HTMLInputElement).name;

        setPinCode({ [name]: "" });

        const nextFocusedInput = Math.max(1, +name - 1);

        setFocusedInput(`${nextFocusedInput}`);

        break;
      }

      default: {
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (!isInWebView) {
      return;
    }

    const ids = Object.keys(defaultPinCode);

    ids.forEach((id) => {
      const element = document.querySelector<HTMLInputElement>(`[id='${id}']`);

      element?.addEventListener("beforeinput", onBeforeInput);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      ids.forEach((id) => {
        const element = document.querySelector<HTMLInputElement>(
          `[id='${id}']`,
        );

        element?.removeEventListener("beforeinput", onBeforeInput);
      });
    };
  }, []);

  return (
    <>
      {([1, 2, 3, 4, 5, 6] as const).map((i) => {
        const isPinBlank = pinCode[i].toString() === "";

        return (
          <PinInputField
            key={i}
            id={`${i}`}
            name={`${i}`}
            min={0}
            max={9}
            inputMode="numeric"
            type={isVirtualKeyboardEnabled || !showValues ? "text" : "number"}
            className="pin__input"
            autoComplete="chrome-off"
            onFocus={onFocus}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onPasteCapture={onPasteCapture}
            value={isPinBlank
              ? ""
              : showValues
                ? pinCode[i]
                : "*"
            }
            maxLength={1}
            pattern="[0-9]*"
          />
        );
      })}
    </>
  );
};

export default PinInputs;
